import * as React from "react";
import {Spinner, SpinnerSize} from "office-ui-fabric-react/lib/Spinner";
import {DmsSearch, EllipsisOptions, FilingLocation, PaginatedFilingLocations} from "../models";
import ResultsBrowser from "./ResultsBrowser";
import LocationItemTabView from "./LocationItemTabView";
import {DocumentSearch} from "../search/DocumentSearch";

export interface LocationItemsListTabViewState {
    isExpanded: boolean;
    expandedParent: FilingLocation;
    expandedResults: FilingLocation[];
    locations: PaginatedFilingLocations;
    moreResults: boolean;
}

export interface LocationItemsListTabViewProps {
    onChooseLocation: (location: FilingLocation) => Promise<void>;
    chooseThisLocationText: string;
    locations: PaginatedFilingLocations;
    isSearching: boolean;
    onExpand: (location: FilingLocation) => void;
    setExpandedResults: (location: FilingLocation[]) => void;
    noItemsText?: string;
    showResults?: boolean;
    searchPhrase: string;
    moreResults: boolean;
    folderSearch: boolean; // needed for pagination
    localDocs: boolean;
    teamsId: string;
    channelId: string;
    storageType: string;
    userId: string;
    tenantId: string;
    errorMessage: string;
    TabView: boolean;
    ShowCheckinPanel: (documentInfo: DmsSearch, share: boolean) => void;
    expandedResults: FilingLocation[];
    onBack: () => void;
    darkMode: boolean;
    searchFilter: string;
    checkoutToChannel: boolean;
    ShowFilingButton: (value: boolean, location: FilingLocation) => void;
    expandedFolderResults: FilingLocation[];
    shouldIncludeCheckedOutFiles: boolean;
    searchAllDms: boolean;
    clientCollabLicensed: boolean;
    EllipsisOptionOnClick: (documentInfo: DmsSearch, option: EllipsisOptions) => void;
    privateChannel: boolean;
    isLitigationHoldEnabled: boolean;
    isFrictionlessSpExperienceEnabled: boolean;
}

export class LocationItemsListTabView extends React.Component<
    LocationItemsListTabViewProps,
    LocationItemsListTabViewState
> {
    private documentSearch = new DocumentSearch();

    constructor(props: LocationItemsListTabViewProps) {
        super(props);

        this.state = {
            isExpanded: false,
            expandedParent: null,
            expandedResults: [],
            locations: this.props.locations,
            moreResults: this.props.moreResults
        };
    }

    componentDidMount() {
        if (!this.props.localDocs) {
            document.addEventListener("scroll", this.trackScrolling);
        } else {
            const filteredDocs: FilingLocation[] = [];

            for (let i = 0; i < this.state.locations.Page.length; i++) {
                if (
                    (this.state.locations.Page[i].Metadata["InTeams"] == "Yes (Changed Locally)" ||
                        this.state.locations.Page[i].Metadata["InDMS"] == "No") &&
                    this.state.locations.Page[i].IsFile
                ) {
                    filteredDocs.push(this.state.locations.Page[i]);
                }
            }
        }
    }

    componentWillUnmount() {
        if (!this.props.localDocs) {
            document.removeEventListener("scroll", this.trackScrolling);
        }
    }

    isBottom(el: HTMLCollectionOf<Element>) {
        return Math.floor(el[0].getBoundingClientRect().bottom) <= window.innerHeight && this.state.moreResults == true;
    }

    trackScrolling = () => {
        const wrappedElement = document.getElementsByClassName("location-list-container");
        if (this.isBottom(wrappedElement)) {
            this.setState({moreResults: false});
            this.documentSearch.runSearchNow(
                this.props.userId,
                this.props.tenantId,
                this.props.teamsId,
                this.props.channelId,
                this.props.storageType,
                this.props.searchPhrase,
                this.props.shouldIncludeCheckedOutFiles,
                this.props.searchAllDms,
                false,
                this.props.folderSearch,
                null, 
                this.addNewResultsToPage,
                (errorMessage) => {
                    throw Error(errorMessage);
                },
                this.props.searchFilter,
                this.state.locations.PagingToken
            );
        }
    };

    private addNewResultsToPage = (results: PaginatedFilingLocations) => {
        const scrollbarHeight = document.body.scrollHeight;
        const newLocations = this.state.locations.Page;
        const responseLocations = results.Page;

        Array.prototype.push.apply(newLocations, responseLocations);
        this.setState(
            {
                locations: {
                    Page: newLocations,
                    PagingToken: results.PagingToken,
                    Errors: null,
                },
            },
            () => {
                if (scrollbarHeight < document.body.scrollHeight) {
                    this.setState({moreResults: true});
                }

                if (this.props.folderSearch) {
                    // when in attach file mode and performing a folder search, the results shouldn't be clickable, only allowed to browse into
                    const result = this.state.locations;

                    for (let i = 0; i < this.state.locations.Page.length; i++) {
                        result.Page[i].CanFileDirect = false;
                    }

                    this.setState({locations: result});
                }
            }
        );
    };

    componentDidUpdate(prevProps: LocationItemsListTabViewProps, _: LocationItemsListTabViewState) {
        if (this.props.expandedResults !== prevProps.expandedResults && this.props.localDocs) {
            this.setState({expandedResults: this.props.expandedResults});
        }

        if (this.props.locations !== prevProps.locations) {
            this.setState({locations: this.props.locations, moreResults: this.props.moreResults});
        }

        if (this.props.expandedFolderResults != prevProps.expandedFolderResults) {
            this.setState({expandedResults: this.props.expandedFolderResults});
        }
    }

    expandedLocation = (parent: FilingLocation, children: FilingLocation[]) => {
        this.setState({
            expandedParent: parent,
            expandedResults: children,
            isExpanded: true,
        });

        this.props.onExpand(parent);
        this.props.setExpandedResults(children);
    };

    chooseLocation = (location: FilingLocation) => (onComplete: () => void) => {
        this.setState({
            expandedParent: null,
            expandedResults: [],
            isExpanded: false,
        });

        this.props.onChooseLocation(location).then(() => {
            if (onComplete != null) {
                onComplete();
            }
        });
    };

    chooseBrowseLocation = (location: FilingLocation) => (onComplete: () => void) => {
        this.setState({
            expandedParent: null,
            expandedResults: [],
            isExpanded: false,
        });

        this.props.onChooseLocation(location).then(() => {
            if (onComplete != null) {
                onComplete();
            }
        });
    };

    shouldShowResults = (): boolean => (this.props.showResults === false ? false : true);

    hasSearchResults = (): boolean =>
        this.props.locations != null ? (this.props.locations.Page.length > 0 ? true : false) : false;

    private isBrowseMode = (): boolean => {
        return this.props.searchPhrase == null || this.props.searchPhrase == "";
    };

    render() {
        return (
            <div>
                {this.props.isSearching && <Spinner size={SpinnerSize.medium} label="Loading..." />}

                {this.shouldShowResults() &&
                    !this.state.isExpanded &&
                    this.state.locations != null &&
                    !this.props.localDocs && (
                        <div className="location-list-container">
                            <LocationItemTabView
                                Locations={this.state.locations.Page}
                                Expanded={this.expandedLocation}
                                FileTo={(l) => this.props.onChooseLocation(l)}
                                loading={false}
                                localDocs={this.props.localDocs}
                                teamsId={this.props.teamsId}
                                channelId={this.props.channelId}
                                storageType={this.props.storageType}
                                UserId={this.props.userId}
                                tenantId={this.props.tenantId}
                                ShowCheckinPanel={this.props.ShowCheckinPanel}
                                darkMode={this.props.darkMode}
                                checkoutToChannel={this.props.checkoutToChannel}
                                ShowFilingButton={this.props.ShowFilingButton}
                                clientCollabLicensed={this.props.clientCollabLicensed}
                                isBrowseMode={this.isBrowseMode()}
                                shouldIncludeCheckedOutFiles={this.props.shouldIncludeCheckedOutFiles}
                                EllipsisOptionOnClick={this.props.EllipsisOptionOnClick}
                                expandedParent={this.state.expandedParent}
                                privateChannel={this.props.privateChannel}
                                isLitigationHoldEnabled={this.props.isLitigationHoldEnabled}
                                isFrictionlessSpExperienceEnabled={this.props.isFrictionlessSpExperienceEnabled}
                            />

                            {!this.props.isSearching && !this.hasSearchResults() && this.props.errorMessage == null && (
                                <div>
                                    {this.props.noItemsText ? this.props.noItemsText : "There are no search results"}
                                </div>
                            )}
                        </div>
                    )}

                {this.shouldShowResults() &&
                    !this.state.isExpanded &&
                    this.state.locations != null &&
                    this.props.localDocs && (
                        <div className="location-list-container">
                            {
                                <LocationItemTabView
                                    Locations={this.state.locations.Page}
                                    Expanded={this.expandedLocation}
                                    FileTo={() => {}}
                                    loading={false}
                                    localDocs={this.props.localDocs}
                                    teamsId={this.props.teamsId}
                                    channelId={this.props.channelId}
                                    storageType={this.props.storageType}
                                    UserId={this.props.userId}
                                    tenantId={this.props.tenantId}
                                    ShowCheckinPanel={this.props.ShowCheckinPanel}
                                    darkMode={this.props.darkMode}
                                    checkoutToChannel={this.props.checkoutToChannel}
                                    ShowFilingButton={this.props.ShowFilingButton}
                                    clientCollabLicensed={this.props.clientCollabLicensed}
                                    isBrowseMode={this.isBrowseMode()}
                                    shouldIncludeCheckedOutFiles={this.props.shouldIncludeCheckedOutFiles}
                                    EllipsisOptionOnClick={this.props.EllipsisOptionOnClick}
                                    expandedParent={this.state.expandedParent}
                                    privateChannel={this.props.privateChannel}
                                    isLitigationHoldEnabled={this.props.isLitigationHoldEnabled}
                                    isFrictionlessSpExperienceEnabled={this.props.isFrictionlessSpExperienceEnabled}
                                />
                            }
                            {!this.props.isSearching && !this.hasSearchResults() && (
                                <div style={{color: this.props.darkMode ? "white" : "black"}}>
                                    {this.props.noItemsText ? this.props.noItemsText : "Files area is empty"}
                                </div>
                            )}
                        </div>
                    )}

                {this.state.isExpanded && (
                    <ResultsBrowser
                        onChooseLocation={(l) => this.props.onChooseLocation(l)}
                        backToTheStart={() => {
                            this.setState({isExpanded: false});
                        }}
                        chooseThisLocationText={this.props.chooseThisLocationText}
                        isSearching={this.props.isSearching}
                        rootParent={this.state.expandedParent}
                        rootChildren={this.state.expandedResults}
                        localDocs={this.props.localDocs}
                        storageType={this.props.storageType}
                        userId={this.props.userId}
                        tenantId={this.props.tenantId}
                        teamsId={this.props.teamsId}
                        channelId={this.props.channelId}
                        TabView={this.props.TabView}
                        ShowCheckinPanel={this.props.ShowCheckinPanel}
                        onExpand={(parent, children) => {
                            this.props.onExpand(parent);
                            this.props.setExpandedResults(children);
                        }}
                        onBack={this.props.onBack}
                        darkMode={this.props.darkMode}
                        checkoutToChannel={this.props.checkoutToChannel}
                        ShowFilingButton={this.props.ShowFilingButton}
                        shouldIncludeCheckedOutFiles={this.props.shouldIncludeCheckedOutFiles}
                        clientCollabLicensed={this.props.clientCollabLicensed}
                        EllipsisOptionOnClick={this.props.EllipsisOptionOnClick}
                        privateChannel={this.props.privateChannel}
                        isLitigationHoldEnabled={this.props.isLitigationHoldEnabled}
                        isFrictionlessSpExperienceEnabled={this.props.isFrictionlessSpExperienceEnabled}
                    />
                )}
            </div>
        );
    }
}

export default LocationItemsListTabView;
